import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useProfileCompletion } from "@/hooks/use-profile-completion";
import { ProfilePromptModal } from "@/components/ProfilePromptModal";
import { GhostClaimModal } from "@/components/GhostClaimModal";
import { SignInButton } from "@clerk/clerk-react";
import { useLocation, Link } from "wouter";
import { BottomNav } from "@/components/BottomNav";
import { HistoryRoundRow } from "@/components/ledger/HistoryRoundRow";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Trophy,
  Skull,
  Users,
  Calendar,
  ChevronRight,
  ChevronDown,
  Loader2,
  CreditCard,
  Smartphone,
  Banknote,
  CircleDollarSign,
  LogIn,
  Swords,
  Info,
  Crown,
  Medal,
  Target,
  Flame,
  ArrowUp,
  ArrowDown,
  Minus,
  BarChart3,
  UserPlus,
  Share2,
  X,
  Check,
} from "lucide-react";

type TimeFilter = "month" | "ytd" | "year";

const FILTER_LABELS: Record<TimeFilter, string> = {
  month: "Month",
  ytd: "YTD",
  year: new Date().getFullYear().toString(),
};

function getNetLabel(filter: TimeFilter): string {
  switch (filter) {
    case "month":
      return "Month Net";
    case "ytd":
      return "YTD Net";
    case "year":
      return `${new Date().getFullYear()} Net`;
  }
}

interface RoundHistoryItem {
  id: string;
  date: string;
  courseName: string;
  gameMode: string;
  engineType?: string;
  matchFormat: string;
  status: string;
  netResult: number;
  stakeFront: number;
  stakeBack: number;
  stakeOverall: number;
  payoutType?: string;
  isRivalry: boolean;
  rivalryContext?: string | null;
  teamHolesWon: {
    myTeamHolesWon: number;
    opponentHolesWon: number;
    tiedHoles: number;
    myCarriers: { carriers: Record<string, { name: string; carries: number }>; shared: number } | null;
    opponentCarriers: { carriers: Record<string, { name: string; carries: number }>; shared: number } | null;
  } | null;
  opponents: { displayName: string; personId: string }[];
  teammates: { displayName: string; personId: string }[];
  participants: { displayName: string; personId: string; team: string; handicap: string; isWinner?: boolean }[];
  settlements: {
    id: string;
    payerName: string;
    payeeName: string;
    amount: number;
    status: string;
    paymentMethod: string | null;
  }[];
  ledgerLines: {
    betType: string;
    amount: number;
    fromPersonId: string;
    toPersonId: string;
    holeNumber: number | null;
  }[];
}

interface PlayerStats {
  net: number;
  pressWinPct: number;
  wins: number;
  losses: number;
  ties: number;
  frequentFoe: {
    personId: string;
    name: string;
    roundCount: number;
    netAgainst: number;
  } | null;
  biggestWin: number;
  biggestLoss: number;
  roundsPlayed: number;
}

type SeasonWindow = "45d" | "ytd" | "alltime";

const SEASON_WINDOW_LABELS: Record<SeasonWindow, string> = {
  "45d": "45 Days",
  ytd: "This Year",
  alltime: "All Time",
};

interface PlayerStatsV2 {
  playerId: string;
  displayName: string;
  window: string;
  dollars: {
    netAllTime: number;
    netYTD: number;
    net45Day: number;
    avgSwingPerRound: number;
  };
  bettingTier: string;
  rounds: { total: number; wins: number; losses: number; ties: number; winPct: number };
  pressRecord: { wins: number; losses: number; ties: number; winPct: number };
  legRecord: {
    front: { won: number; lost: number; tied: number };
    back: { won: number; lost: number; tied: number };
    overall: { won: number; lost: number; tied: number };
  };
  strokeUtilization: {
    withStrokeHoles: number;
    withStrokeWins: number;
    withStrokeLosses: number;
    withStrokeTies: number;
    withStrokeWinPct: number | null;
    straightUpHoles: number;
    straightUpWins: number;
    straightUpLosses: number;
    straightUpTies: number;
    straightUpWinPct: number | null;
  };
  recentForm: ("W" | "L" | "T")[];
  currentStreak: { type: "W" | "L" | "T"; count: number } | null;
  rivalries: {
    personId: string;
    displayName: string;
    rounds: number;
    wins: number;
    losses: number;
    ties: number;
    net: number;
    lastPlayed: string | null;
  }[];
  formatNote: null;
}

interface SettleItem {
  settlementId: string;
  roundId: string;
  flow: "outgoing" | "incoming";
  counterpartyName: string;
  counterpartyPersonId: string;
  amount: number;
  courseName: string;
  date: string;
  matchFormat: string;
  venmoHandle: string | null;
  paypalHandle: string | null;
}

function formatMatchup(
  teammates: { displayName: string }[],
  opponents: { displayName: string }[]
): string {
  const myTeam = teammates.length > 0
    ? `You & ${teammates.map((t) => t.displayName).join(" & ")}`
    : "You";
  const theirTeam = opponents.map((o) => o.displayName).join(" & ");
  return `${myTeam} vs ${theirTeam}`;
}

function formatMoney(amount: number): string {
  const abs = Math.abs(amount);
  return `$${abs % 1 === 0 ? abs.toFixed(0) : abs.toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatFullDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function betTypeLabel(betType: string): string {
  switch (betType) {
    case "front": return "Front 9";
    case "back": return "Back 9";
    case "overall": return "Overall";
    case "press": return "Press";
    default: return betType;
  }
}

function SkeletonCard() {
  return (
    <div className="animate-pulse bg-gray-100 rounded-xl h-24 mb-3" />
  );
}

function SkeletonList() {
  return (
    <div className="space-y-3">
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );
}

interface GroupOption {
  groupId: string;
  groupName: string;
  typicalStakesTier: string | null;
}

export default function Ledger() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [, navigate] = useLocation();

  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get("tab") === "standings" ? "standings" as const : "history" as const;
  const initialGroupId = urlParams.get("groupId");
  const initialClaimedRoundId = urlParams.get("claimedRoundId");

  const [activeTab, setActiveTab] = useState<"history" | "season" | "analytics" | "standings">(initialTab);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("ytd");
  const [seasonWindow, setSeasonWindow] = useState<SeasonWindow>("ytd");
  const [selectedRound, setSelectedRound] = useState<RoundHistoryItem | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [inviteOverlayOpen, setInviteOverlayOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [showGhostClaim, setShowGhostClaim] = useState(false);
  const [claimedSuccess, setClaimedSuccess] = useState(!!initialClaimedRoundId);
  const [highlightRoundId] = useState(initialClaimedRoundId);
  const { shouldShowOnAppOpen, shouldShowOnRoute, dismiss: dismissProfilePrompt } = useProfileCompletion();

  useEffect(() => {
    if (!initialClaimedRoundId) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("claimedRoundId");
    window.history.replaceState({}, "", url.toString());
  }, []);

  useEffect(() => {
    if (shouldShowOnAppOpen) setShowProfilePrompt(true);
  }, [shouldShowOnAppOpen]);

  useEffect(() => {
    if ((activeTab === "analytics" || activeTab === "standings" || activeTab === "season") && shouldShowOnRoute) {
      setShowProfilePrompt(true);
    }
  }, [activeTab, shouldShowOnRoute]);

  const personId = (user as any)?.personId as string | null;

  const fetchJson = async (url: string) => {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
    return res.json();
  };

  const { data: userGroupsData } = useQuery<{ groups: GroupOption[] }>({
    queryKey: ["user-groups"],
    queryFn: () => fetchJson("/api/user/groups"),
    enabled: !!personId,
  });

  const userGroups = userGroupsData?.groups ?? [];
  const defaultGroupId = userGroups[0]?.groupId ?? null;

  const { data: activeRoundData } = useQuery<{ activeRound: { id: string; courseName: string; matchFormat: string; gameMode: string; createdAt: string; userId: string } | null }>({
    queryKey: ["user-active-rounds"],
    queryFn: () => fetchJson("/api/user/active-rounds"),
    enabled: !!personId,
  });

  const activeRound = activeRoundData?.activeRound ?? null;
  const showActiveMatchBanner = activeRound != null && activeRound.userId !== user?.id;

  const handleHeaderInvite = async () => {
    const groupId = defaultGroupId;
    if (!groupId) return;
    setInviteLoading(true);
    try {
      const res = await fetch(`/api/groups/${groupId}/invite`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("Failed to create invite");
      const data = await res.json();
      const inviteUrl = `${window.location.origin}/invite/${data.inviteToken}`;

      if (navigator.share) {
        await navigator.share({
          title: "Live Match on DoublePress",
          text: "Follow today's match live on DoublePress — every hole, every bet, in real time.",
          url: inviteUrl,
        });
      } else {
        await navigator.clipboard.writeText(inviteUrl);
        setInviteCopied(true);
        setTimeout(() => setInviteCopied(false), 3000);
      }
      setInviteOverlayOpen(false);
    } catch (err: any) {
      if (err.name !== "AbortError") {
        try {
          await navigator.clipboard.writeText(`${window.location.origin}/invite/error`);
        } catch {}
      }
      setInviteOverlayOpen(false);
    } finally {
      setInviteLoading(false);
    }
  };

  const { data: roundsData, isLoading: roundsLoading } = useQuery<{ rounds: RoundHistoryItem[] }>({
    queryKey: ["ledger-rounds", personId, timeFilter],
    queryFn: () => fetchJson(`/api/ledger/player/${personId}/rounds?filter=${timeFilter}`),
    enabled: !!personId,
  });

  const { data: statsData, isLoading: statsLoading } = useQuery<PlayerStats>({
    queryKey: ["ledger-stats", personId, timeFilter],
    queryFn: () => fetchJson(`/api/ledger/player/${personId}/stats?filter=${timeFilter}`),
    enabled: !!personId,
  });

  const { data: statsV2Data, isLoading: statsV2Loading } = useQuery<PlayerStatsV2>({
    queryKey: ["player-stats-v2", personId, seasonWindow],
    queryFn: () => fetchJson(`/api/players/${personId}/stats?window=${seasonWindow}`),
    enabled: !!personId && (activeTab === "season" || activeTab === "analytics"),
  });

  const { data: settleData, isLoading: settleLoading } = useQuery<{ pending: any[] }>({
    queryKey: ["ledger-settle", personId],
    queryFn: () => fetchJson(`/api/ledger/player/${personId}/pending`),
    enabled: !!personId,
    select: (data) => ({
      pending: data.pending.map((s: any): SettleItem => {
        const isOutgoing = s.direction === "owe";
        return {
          settlementId: s.settlementId,
          roundId: s.roundId,
          flow: isOutgoing ? "outgoing" : "incoming",
          counterpartyName: s.counterpartyName,
          counterpartyPersonId: s.counterpartyPersonId,
          amount: s.amount,
          courseName: s.courseName,
          date: s.date,
          matchFormat: s.matchFormat,
          venmoHandle: isOutgoing ? s.payeeVenmo : s.payerVenmo,
          paypalHandle: isOutgoing ? s.payeePaypal : s.payerPaypal,
        };
      }),
    }),
  });

  const settleItems: SettleItem[] = settleData?.pending ?? [];

  const hasData = (roundsData?.rounds?.length ?? 0) > 0;

  const { data: ghostData } = useQuery<{ matches: { personId: string; displayName: string; roundCount: number }[] }>({
    queryKey: ['/api/persons/ghost-search'],
    queryFn: async () => {
      const res = await fetch('/api/persons/ghost-search', { credentials: 'include' });
      if (!res.ok) throw new Error('ghost search failed');
      return res.json();
    },
    enabled: !!personId && !roundsLoading && !hasData && activeTab === 'history',
    staleTime: 60000,
  });

  const ghostMatches = ghostData?.matches ?? [];

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-800" size={32} />
      </div>
    );
  }

  if (!isAuthenticated || !personId) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <div className="bg-emerald-900 text-white px-4 py-6">
          <h1 className="text-xl font-bold">Razzy Ledger</h1>
          <p className="text-emerald-200 text-sm mt-1">Sign in to view your betting history</p>
        </div>
        <div className="flex flex-col items-center justify-center pt-20 px-6">
          <LogIn size={48} className="text-gray-300 mb-4" />
          <p className="text-gray-500 text-center text-sm mb-6">
            Sign in to track your rounds, stats, and settlements.
          </p>
          <SignInButton mode="modal">
            <button
              className="bg-emerald-800 text-white font-bold py-3 px-8 rounded-xl"
              data-testid="button-sign-in-ledger"
            >
              Sign In
            </button>
          </SignInButton>
        </div>
        <BottomNav />
      </div>
    );
  }

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const res = await fetch("/api/dev/seed-ledger?count=10", { method: "POST", credentials: "include" });
      if (res.ok) {
        window.location.reload();
      }
    } catch {
    } finally {
      setSeeding(false);
    }
  };

  const handleResetSeed = async () => {
    try {
      await fetch("/api/dev/reset-ledger-seed", { method: "POST", credentials: "include" });
      window.location.reload();
    } catch {}
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-emerald-900 text-white px-4 pb-3 safe-area-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold" data-testid="text-ledger-title">Razzy Ledger</h1>
            <a href="/razzy-faq" className="text-emerald-300 active:text-[#FACC15]" data-testid="link-razzy-faq">
              <Info className="w-4 h-4" />
            </a>
          </div>
          <div className="flex items-center gap-3">
          {defaultGroupId && (
            <button
              onClick={() => setInviteOverlayOpen(true)}
              className="text-emerald-200 active:text-[#FACC15] p-1"
              data-testid="button-header-invite"
            >
              <UserPlus className="w-5 h-5" />
            </button>
          )}
          {(user as any)?.isAdmin && !hasData && !roundsLoading && (
            <button
              onClick={handleSeedData}
              disabled={seeding}
              className="bg-yellow-400 text-emerald-900 text-xs font-bold px-3 py-1.5 rounded-lg disabled:opacity-50"
              data-testid="button-seed-data"
            >
              {seeding ? "Loading..." : "Load Demo Data"}
            </button>
          )}
          {(user as any)?.isAdmin && hasData && (
            <button
              onClick={handleResetSeed}
              className="text-emerald-300 text-xs font-medium underline"
              data-testid="button-reset-seed"
            >
              Reset Demo
            </button>
          )}
          </div>
        </div>
      </div>

      <div className="bg-emerald-900 px-4 pb-2">
        <div className="flex gap-2">
          {(["month", "ytd", "year"] as TimeFilter[]).map((f) => (
            <button
              key={f}
              onClick={() => setTimeFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                timeFilter === f
                  ? "bg-yellow-400 text-emerald-900"
                  : "bg-emerald-800/60 text-emerald-200"
              }`}
              data-testid={`filter-${f}`}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      {!hasData && !roundsLoading && ghostMatches.length > 0 && activeTab === 'history' && (
        <button
          onClick={() => setShowGhostClaim(true)}
          className="w-full text-left block"
          data-testid="banner-find-past-rounds"
        >
          <div className="bg-yellow-400 px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-emerald-900 text-sm font-bold">Find Your Past Rounds</p>
              <p className="text-emerald-800 text-xs">Rounds played before you joined may be waiting for you</p>
            </div>
            <span className="text-emerald-900 text-xs font-bold px-3 py-1.5 rounded-lg bg-emerald-900/10 whitespace-nowrap">
              Link Rounds →
            </span>
          </div>
        </button>
      )}

      {showActiveMatchBanner && (
        <Link
          href={`/rounds/${activeRound.id}/live`}
          className="block"
          data-testid="banner-active-match"
        >
          <div className="bg-emerald-700 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400" />
              </span>
              <div>
                <p className="text-white text-sm font-bold" data-testid="text-active-match-title">Live Match in Progress</p>
                <p className="text-emerald-200 text-xs">{activeRound.courseName} &middot; {activeRound.matchFormat}</p>
              </div>
            </div>
            <span className="bg-yellow-400 text-emerald-900 text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap" data-testid="button-join-scorecard">
              Join Scorecard
            </span>
          </div>
        </Link>
      )}

      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 safe-area-top">
        <div className="flex">
          {(["history", "season", "analytics", "standings"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-center text-sm font-bold transition-colors relative ${
                activeTab === tab
                  ? "text-emerald-800"
                  : "text-gray-400"
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-800" />
              )}
            </button>
          ))}
        </div>
      </div>

      {claimedSuccess && activeTab === "history" && (
        <div className="bg-emerald-700 px-4 py-3 flex items-center justify-between" data-testid="banner-claimed-success">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-white flex-shrink-0" />
            <p className="text-white text-sm font-semibold">Round added to your Ledger</p>
          </div>
          <button onClick={() => setClaimedSuccess(false)} className="text-emerald-200 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="px-4 pt-4">
        {activeTab === "history" && (
          <HistoryTab
            rounds={roundsData?.rounds ?? []}
            isLoading={roundsLoading}
            personId={personId}
            onSelectRound={(r) => navigate(`/rounds/${r.id}/summary`)}
            highlightRoundId={highlightRoundId}
          />
        )}
        {activeTab === "season" && (
          <SeasonTab
            stats={statsData ?? null}
            statsV2={statsV2Data ?? null}
            isLoading={statsLoading || statsV2Loading}
            timeFilter={timeFilter}
            seasonWindow={seasonWindow}
            onSeasonWindowChange={setSeasonWindow}
          />
        )}
        {activeTab === "analytics" && (
          <AnalyticsTab
            statsV2={statsV2Data ?? null}
            isLoading={statsV2Loading}
            seasonWindow={seasonWindow}
            onSeasonWindowChange={setSeasonWindow}
          />
        )}
        {activeTab === "standings" && (
          <StandingsTab
            personId={personId}
            timeFilter={timeFilter}
            onPlayerTap={(pid: string) => navigate(`/player/${pid}`)}
            initialGroupId={initialGroupId}
          />
        )}
      </div>

      <RoundDetailSheet
        round={selectedRound}
        personId={personId}
        onClose={() => setSelectedRound(null)}
        onPlayerTap={(pid: string) => {
          setSelectedRound(null);
          navigate(`/player/${pid}`);
        }}
        onRivalryTap={(url: string) => {
          setSelectedRound(null);
          navigate(url);
        }}
      />

      {inviteOverlayOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => setInviteOverlayOpen(false)}
          data-testid="invite-overlay"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md p-6 overflow-y-auto -webkit-overflow-scrolling-touch"
            style={{ maxHeight: '80vh', paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-2">Invite to Group</h3>
            <p className="text-sm text-gray-500 mb-5">Share a link so anyone can join your group and follow live scorecards.</p>
            <button
              onClick={handleHeaderInvite}
              disabled={inviteLoading}
              className="w-full bg-emerald-900 text-white font-bold py-3 rounded-xl active:bg-emerald-800 disabled:opacity-50"
              data-testid="button-send-invite"
            >
              {inviteLoading ? "Sharing…" : "Share Invite Link"}
            </button>
          </div>
        </div>
      )}

      {inviteCopied && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs font-medium px-4 py-2 rounded-full z-50 shadow-lg"
          data-testid="toast-invite-copied"
        >
          Invite link copied!
        </div>
      )}

      <BottomNav />

      <ProfilePromptModal
        open={showProfilePrompt}
        onDismiss={() => { dismissProfilePrompt(); setShowProfilePrompt(false); }}
      />

      <GhostClaimModal
        open={showGhostClaim}
        onDismiss={() => setShowGhostClaim(false)}
        onClaimed={() => {
          setShowGhostClaim(false);
        }}
      />
    </div>
  );
}

function HistoryTab({
  rounds,
  isLoading,
  personId,
  onSelectRound,
  highlightRoundId,
}: {
  rounds: RoundHistoryItem[];
  isLoading: boolean;
  personId: string;
  onSelectRound: (r: RoundHistoryItem) => void;
  highlightRoundId?: string | null;
}) {
  if (isLoading) return <SkeletonList />;

  if (rounds.length === 0) {
    return (
      <div className="text-center pt-16" data-testid="empty-history">
        <Calendar size={40} className="mx-auto text-gray-300 mb-3" />
        <p className="text-gray-400 font-semibold text-sm">No rounds in this window</p>
        <p className="text-gray-500 text-xs mt-1">Play a round or adjust the time filter</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="history-feed">
      {rounds.map((r) => (
        <div key={r.id} className={r.id === highlightRoundId ? 'ring-2 ring-emerald-400 rounded-xl' : ''}>
          <HistoryRoundRow
            round={r}
            onClick={() => onSelectRound(r)}
          />
        </div>
      ))}
    </div>
  );
}

function getWindowedNet(statsV2: PlayerStatsV2 | null, window: SeasonWindow): number {
  if (!statsV2) return 0;
  if (window === "45d") return statsV2.dollars.net45Day;
  if (window === "alltime") return statsV2.dollars.netAllTime;
  return statsV2.dollars.netYTD;
}

function getWindowNetLabel(window: SeasonWindow): string {
  if (window === "45d") return "45 Day Net";
  if (window === "alltime") return "All-Time Net";
  return "YTD Net";
}

function SeasonTab({
  stats,
  statsV2,
  isLoading,
  timeFilter,
  seasonWindow,
  onSeasonWindowChange,
}: {
  stats: PlayerStats | null;
  statsV2: PlayerStatsV2 | null;
  isLoading: boolean;
  timeFilter: TimeFilter;
  seasonWindow: SeasonWindow;
  onSeasonWindowChange: (w: SeasonWindow) => void;
}) {
  if (isLoading) return <SkeletonList />;

  const hasData = statsV2
    ? statsV2.rounds.total > 0
    : stats !== null && stats.roundsPlayed > 0;

  if (!hasData) {
    return (
      <div className="text-center pt-16" data-testid="empty-season">
        <TrendingUp size={40} className="mx-auto text-gray-300 mb-3" />
        <p className="text-gray-400 font-semibold text-sm">No stats yet</p>
        <p className="text-gray-500 text-xs mt-1">Finalize rounds to see your season stats</p>
      </div>
    );
  }

  const windowedNet = statsV2 ? getWindowedNet(statsV2, seasonWindow) : (stats?.net ?? 0);
  const wRecord = statsV2
    ? `${statsV2.rounds.wins}-${statsV2.rounds.losses}-${statsV2.rounds.ties}`
    : stats
    ? `${stats.wins}-${stats.losses}-${stats.ties}`
    : "0-0-0";
  const pressWinPct = statsV2 ? statsV2.pressRecord.winPct : (stats?.pressWinPct ?? 0);
  const roundsPlayed = statsV2 ? statsV2.rounds.total : (stats?.roundsPlayed ?? 0);

  return (
    <div className="space-y-3" data-testid="season-stats">
      {/* Season-specific window filter */}
      <div className="flex gap-2">
        {(["45d", "ytd", "alltime"] as SeasonWindow[]).map((w) => (
          <button
            key={w}
            onClick={() => onSeasonWindowChange(w)}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
              seasonWindow === w
                ? "bg-emerald-800 text-white"
                : "bg-gray-100 text-gray-500"
            }`}
            data-testid={`season-filter-${w}`}
          >
            {SEASON_WINDOW_LABELS[w]}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-xs text-gray-500 font-medium">{getWindowNetLabel(seasonWindow)}</p>
              {statsV2?.bettingTier && (
                <span
                  className="text-xs font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full"
                  data-testid="badge-betting-tier"
                >
                  {statsV2.bettingTier}
                </span>
              )}
            </div>
            <p
              className={`text-3xl font-black ${
                windowedNet > 0 ? "text-green-600" : windowedNet < 0 ? "text-red-500" : "text-gray-600"
              }`}
              data-testid="text-season-net"
            >
              {windowedNet > 0 ? "+" : ""}{formatMoney(windowedNet)}
            </p>
          </div>
          <DollarSign size={28} className="text-gray-200" />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-sm font-bold text-gray-500">Record</p>
            <p className="font-bold text-lg text-gray-800" data-testid="text-wl-record">
              {wRecord}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-sm font-bold text-gray-500">Press Win %</p>
            <p className="font-bold text-lg text-gray-800" data-testid="text-press-pct">
              {pressWinPct}%
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-sm font-bold text-gray-500">Rounds</p>
            <p className="font-bold text-lg text-gray-800" data-testid="text-rounds-played">
              {roundsPlayed}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Trophy size={14} className="text-yellow-500" />
            <p className="text-xs text-gray-500 font-medium">Biggest Win</p>
          </div>
          <p className="font-bold text-lg text-green-600" data-testid="text-biggest-win">
            +{formatMoney(stats?.biggestWin ?? 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Skull size={14} className="text-red-400" />
            <p className="text-xs text-gray-500 font-medium">Worst Loss</p>
          </div>
          <p className="font-bold text-lg text-red-500" data-testid="text-biggest-loss">
            -{formatMoney(Math.abs(stats?.biggestLoss ?? 0))}
          </p>
        </div>
      </div>

      {stats?.frequentFoe && (
        <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="card-frequent-foe">
          <div className="flex items-center gap-2 mb-2">
            <Users size={14} className="text-purple-500" />
            <p className="text-xs text-gray-500 font-medium">Frequent Foe</p>
          </div>
          <p className="font-bold text-sm text-gray-800">{stats.frequentFoe.name}</p>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-gray-500">
              {stats.frequentFoe.roundCount} round{stats.frequentFoe.roundCount !== 1 ? "s" : ""}
            </span>
            <span
              className={`text-xs font-bold ${
                stats.frequentFoe.netAgainst > 0
                  ? "text-green-600"
                  : stats.frequentFoe.netAgainst < 0
                  ? "text-red-500"
                  : "text-gray-400"
              }`}
            >
              {stats.frequentFoe.netAgainst > 0 ? "+" : ""}
              {formatMoney(stats.frequentFoe.netAgainst)} vs them
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function SettleTab({
  items,
  isLoading,
  personId,
}: {
  items: SettleItem[];
  isLoading: boolean;
  personId: string;
}) {
  if (isLoading) return <SkeletonList />;

  if (items.length === 0) {
    return (
      <div className="text-center pt-16" data-testid="empty-settle">
        <CircleDollarSign size={40} className="mx-auto text-gray-300 mb-3" />
        <p className="text-gray-400 font-semibold text-sm">No rounds to settle</p>
        <p className="text-gray-500 text-xs mt-1">Finalized rounds will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="settle-list">
      {items.map((s) => (
        <SettleCard key={s.settlementId} item={s} />
      ))}
    </div>
  );
}

function AnalyticsTab({
  statsV2,
  isLoading,
  seasonWindow,
  onSeasonWindowChange,
}: {
  statsV2: PlayerStatsV2 | null;
  isLoading: boolean;
  seasonWindow: SeasonWindow;
  onSeasonWindowChange: (w: SeasonWindow) => void;
}) {
  if (isLoading) return <SkeletonList />;

  const su = statsV2?.strokeUtilization ?? null;
  const pr = statsV2?.pressRecord ?? null;

  const hasStrokeData = su !== null && su.withStrokeHoles > 0;
  const pressTotal = pr ? pr.wins + pr.losses + pr.ties : 0;
  const hasPressData = pressTotal >= 3;
  const protectedPct = hasStrokeData
    ? Math.round(((su!.withStrokeWins + su!.withStrokeTies) / su!.withStrokeHoles) * 1000) / 10
    : null;

  const noData = !hasStrokeData && !hasPressData;

  return (
    <div className="space-y-3" data-testid="analytics-tab">
      <div className="flex gap-2">
        {(["45d", "ytd", "alltime"] as SeasonWindow[]).map((w) => (
          <button
            key={w}
            onClick={() => onSeasonWindowChange(w)}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
              seasonWindow === w
                ? "bg-emerald-800 text-white"
                : "bg-gray-100 text-gray-500"
            }`}
            data-testid={`analytics-filter-${w}`}
          >
            {SEASON_WINDOW_LABELS[w]}
          </button>
        ))}
      </div>

      {noData && (
        <div className="text-center pt-16" data-testid="empty-analytics">
          <BarChart3 size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400 font-semibold text-sm">No analytics yet</p>
          <p className="text-gray-500 text-xs mt-1">Play more rounds with handicap strokes and presses to unlock this view.</p>
          <p className="text-gray-500 text-xs mt-1">Finalized rounds without strokes or presses may not produce analytics here.</p>
        </div>
      )}

      {hasStrokeData && (
        <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="card-stroke-utilization">
          <div className="flex items-center gap-2 mb-3">
            <Target size={14} className="text-emerald-700" />
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Stroke Utilization</p>
          </div>
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-3xl font-black text-emerald-700" data-testid="text-protected-pct">
                {protectedPct}%
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Protected (wins + halves)</p>
            </div>
            <p className="text-xs text-gray-500 text-right">
              {su!.withStrokeHoles} stroke hole{su!.withStrokeHoles !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-green-50 rounded-lg p-2 text-center">
              <p className="text-xs text-gray-500">Wins</p>
              <p className="font-bold text-green-600" data-testid="text-stroke-wins">{su!.withStrokeWins}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2 text-center">
              <p className="text-xs text-gray-500">Halved</p>
              <p className="font-bold text-gray-600" data-testid="text-stroke-ties">{su!.withStrokeTies}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-2 text-center">
              <p className="text-xs text-gray-500">Losses</p>
              <p className="font-bold text-red-500" data-testid="text-stroke-losses">{su!.withStrokeLosses}</p>
            </div>
          </div>
        </div>
      )}

      {hasPressData && (
        <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="card-press-performance">
          <div className="flex items-center gap-2 mb-3">
            <Flame size={14} className="text-orange-500" />
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Press Performance</p>
          </div>
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-3xl font-black text-gray-800" data-testid="text-press-win-pct">
                {pr!.winPct}%
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Press win rate</p>
            </div>
            <p className="text-xs text-gray-500 text-right">
              {pressTotal} press{pressTotal !== 1 ? "es" : ""}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-green-50 rounded-lg p-2 text-center">
              <p className="text-xs text-gray-500">Wins</p>
              <p className="font-bold text-green-600" data-testid="text-press-wins">{pr!.wins}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2 text-center">
              <p className="text-xs text-gray-500">Ties</p>
              <p className="font-bold text-gray-600" data-testid="text-press-ties">{pr!.ties}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-2 text-center">
              <p className="text-xs text-gray-500">Losses</p>
              <p className="font-bold text-red-500" data-testid="text-press-losses">{pr!.losses}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface LeaderboardEntry {
  personId: string;
  displayName: string;
  lifetimeNet: number;
  roundsPlayed: number;
  pressWinRate: number;
  roi: number;
  stakesTier: string;
  prestigeScore: number;
  rank: number;
}

interface StandingsHighlight {
  label: string;
  playerName: string;
  personId: string;
  value: string;
  tone: "positive" | "negative" | "neutral";
}

interface StandingsData {
  groupId: string;
  groupName: string;
  filter: string;
  stakesTier: string | null;
  leaderboard: LeaderboardEntry[];
  movement: Record<string, number>;
  highlights: StandingsHighlight[];
}

type StakesTierFilter = "all" | "$1-$9" | "$10-$19" | "$20-$49" | "$50-$99" | "$100+";
const STAKES_FILTER_OPTIONS: { value: StakesTierFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "$1-$9", label: "$1–9" },
  { value: "$10-$19", label: "$10–19" },
  { value: "$20-$49", label: "$20–49" },
  { value: "$50-$99", label: "$50–99" },
  { value: "$100+", label: "$100+" },
];

function StandingsTab({
  personId,
  timeFilter,
  onPlayerTap,
  initialGroupId,
}: {
  personId: string;
  timeFilter: TimeFilter;
  onPlayerTap: (personId: string) => void;
  initialGroupId?: string | null;
}) {
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(initialGroupId ?? null);
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const [stakesTier, setStakesTier] = useState<StakesTierFilter>("all");
  const fetchJson = async (url: string) => {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
    return res.json();
  };

  const { data: groupsData, isLoading: groupsLoading } = useQuery<{ groups: GroupOption[] }>({
    queryKey: ["user-groups"],
    queryFn: () => fetchJson("/api/user/groups"),
  });

  const groups = groupsData?.groups ?? [];
  const activeGroupId = selectedGroupId ?? groups[0]?.groupId ?? null;
  const activeGroup = groups.find((g) => g.groupId === activeGroupId);

  const { data: standingsData, isLoading: standingsLoading, isError } = useQuery<StandingsData>({
    queryKey: ["standings", activeGroupId, timeFilter, stakesTier],
    queryFn: () => fetchJson(`/api/ledger/group/${activeGroupId}?filter=${timeFilter}&stakesTier=${stakesTier}`),
    enabled: !!activeGroupId,
  });

  if (groupsLoading) return <SkeletonList />;

  if (groups.length === 0) {
    return (
      <div className="text-center pt-16" data-testid="empty-standings-no-groups">
        <Users size={40} className="mx-auto text-gray-300 mb-3" />
        <p className="text-gray-400 font-semibold text-sm">No groups yet</p>
        <p className="text-gray-500 text-xs mt-1">
          Groups are created when you play rounds together. Finalize a round to get started.
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center pt-16" data-testid="error-standings">
        <BarChart3 size={40} className="mx-auto text-red-300 mb-3" />
        <p className="text-gray-400 font-semibold text-sm">Couldn't load standings</p>
        <p className="text-gray-300 text-xs mt-1">Try again in a moment</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="standings-tab">
      {groups.length > 1 && (
        <div className="relative" data-testid="group-selector">
          <button
            onClick={() => setGroupDropdownOpen(!groupDropdownOpen)}
            className="w-full flex items-center justify-between bg-white rounded-xl border border-gray-200 px-4 py-3 text-left"
            data-testid="button-group-selector"
          >
            <div>
              <p className="font-bold text-sm text-gray-900">{activeGroup?.groupName ?? "Select Group"}</p>
              {activeGroup?.typicalStakesTier && (
                <p className="text-xs text-gray-500">{activeGroup.typicalStakesTier}</p>
              )}
            </div>
            <ChevronDown size={16} className={`text-gray-400 transition-transform ${groupDropdownOpen ? "rotate-180" : ""}`} />
          </button>
          {groupDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
              {groups.map((g) => (
                <button
                  key={g.groupId}
                  onClick={() => {
                    setSelectedGroupId(g.groupId);
                    setGroupDropdownOpen(false);
                  }}
                  className={`w-full px-4 py-3 text-left text-sm transition-colors ${
                    g.groupId === activeGroupId
                      ? "bg-emerald-50 text-emerald-800 font-bold"
                      : "text-gray-700 active:bg-gray-50"
                  }`}
                  data-testid={`group-option-${g.groupId}`}
                >
                  {g.groupName}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {groups.length === 1 && (
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-3">
          <p className="font-bold text-sm text-gray-900" data-testid="text-group-name">{activeGroup?.groupName}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {timeFilter === "month" ? "This month" : timeFilter === "ytd" ? "Year-to-date" : new Date().getFullYear().toString()} standings
          </p>
        </div>
      )}

      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar" data-testid="stakes-filter">
        {STAKES_FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setStakesTier(opt.value)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
              stakesTier === opt.value
                ? "bg-emerald-800 text-white"
                : "bg-gray-100 text-gray-500 active:bg-gray-200"
            }`}
            data-testid={`stakes-${opt.value}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {standingsLoading ? (
        <SkeletonList />
      ) : standingsData ? (
        <>
          {standingsData.leaderboard.length === 0 ? (
            <div className="text-center pt-12" data-testid="empty-standings">
              <Trophy size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-400 font-semibold text-sm">No standings yet</p>
              <p className="text-gray-500 text-xs mt-1">
                Standings update when rounds are finalized. Play and finalize rounds to see rankings.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5" data-testid="leaderboard-list">
                {standingsData.leaderboard.map((entry) => (
                  <LeaderboardRow
                    key={entry.personId}
                    entry={entry}
                    movement={standingsData.movement[entry.personId] ?? 0}
                    isCurrentUser={entry.personId === personId}
                    onTap={() => onPlayerTap(entry.personId)}
                  />
                ))}
              </div>

              {standingsData.highlights.length > 0 && (
                <div data-testid="standings-highlights">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2 px-1">
                    Spotlight
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {standingsData.highlights.map((h, i) => (
                      <HighlightCard
                        key={i}
                        highlight={h}
                        onTap={() => onPlayerTap(h.personId)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : null}
    </div>
  );
}

function LeaderboardRow({
  entry,
  movement,
  isCurrentUser,
  onTap,
}: {
  entry: LeaderboardEntry;
  movement: number;
  isCurrentUser: boolean;
  onTap: () => void;
}) {
  const isTop3 = entry.rank <= 3;
  const rankIcon = entry.rank === 1
    ? <Crown size={16} className="text-yellow-500" />
    : entry.rank === 2
    ? <Medal size={16} className="text-gray-400" />
    : entry.rank === 3
    ? <Medal size={16} className="text-amber-600" />
    : null;

  return (
    <button
      onClick={onTap}
      className={`w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors active:bg-gray-100 ${
        isCurrentUser
          ? "bg-emerald-50 border border-emerald-200"
          : isTop3
          ? "bg-white border border-gray-100"
          : "bg-white border border-gray-50"
      }`}
      data-testid={`leaderboard-row-${entry.personId}`}
    >
      <div className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 ${
        entry.rank === 1 ? "bg-yellow-100" : entry.rank === 2 ? "bg-gray-100" : entry.rank === 3 ? "bg-amber-50" : "bg-gray-50"
      }`}>
        {rankIcon ?? (
          <span className="text-xs font-bold text-gray-400">{entry.rank}</span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className={`font-bold text-sm truncate ${isCurrentUser ? "text-emerald-800" : "text-gray-900"}`}>
            {entry.displayName}
            {isCurrentUser && <span className="text-xs font-medium text-emerald-600 ml-1">(You)</span>}
          </p>
          <MovementBadge delta={movement} />
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-xs font-bold text-gray-400">{entry.roundsPlayed} rds</span>
          <span className="text-xs font-bold text-gray-400">ROI {entry.roi > 0 ? "+" : ""}{entry.roi.toFixed(0)}%</span>
          <span className="text-xs font-bold text-gray-400">Press {entry.pressWinRate.toFixed(0)}%</span>
        </div>
      </div>

      <div className="text-right shrink-0">
        <p className={`font-black text-sm ${
          entry.lifetimeNet > 0 ? "text-green-600" : entry.lifetimeNet < 0 ? "text-red-500" : "text-gray-400"
        }`}>
          {entry.lifetimeNet > 0 ? "+" : ""}{formatMoney(entry.lifetimeNet)}
        </p>
      </div>
    </button>
  );
}

function MovementBadge({ delta }: { delta: number }) {
  if (delta === 0) {
    return (
      <span className="inline-flex items-center text-xs text-gray-500" data-testid="movement-neutral">
        <Minus size={10} />
      </span>
    );
  }
  if (delta > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-bold text-green-500" data-testid="movement-up">
        <ArrowUp size={10} />
        {delta}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-xs font-bold text-red-400" data-testid="movement-down">
      <ArrowDown size={10} />
      {Math.abs(delta)}
    </span>
  );
}

const HIGHLIGHT_ICONS: Record<string, typeof Trophy> = {
  "Biggest Winner": Trophy,
  "Best ROI": Target,
  "Most Active": Flame,
  "Press Assassin": Swords,
  "Biggest Donor": Skull,
};

const HIGHLIGHT_COLORS: Record<string, string> = {
  positive: "text-green-600",
  negative: "text-red-500",
  neutral: "text-gray-700",
};

function HighlightCard({
  highlight,
  onTap,
}: {
  highlight: StandingsHighlight;
  onTap: () => void;
}) {
  const Icon = HIGHLIGHT_ICONS[highlight.label] ?? Trophy;
  const valueColor = HIGHLIGHT_COLORS[highlight.tone] ?? "text-gray-700";

  return (
    <button
      onClick={onTap}
      className="bg-white rounded-xl border border-gray-100 p-3 text-left active:bg-gray-50 transition-colors"
      data-testid={`highlight-${highlight.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} className="text-gray-400" />
        <p className="text-xs text-gray-500 font-medium">{highlight.label}</p>
      </div>
      <p className={`font-black text-base ${valueColor}`}>{highlight.value}</p>
      <p className="text-xs text-gray-500 font-medium truncate mt-0.5">{highlight.playerName}</p>
    </button>
  );
}

function SettleCard({ item: s }: { item: SettleItem }) {
  const youPay = s.flow === "outgoing";

  return (
    <div
      className="bg-white rounded-xl border border-gray-100 p-4"
      data-testid={`settle-card-${s.settlementId}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div>
          <p className="font-bold text-sm text-gray-800">{s.counterpartyName}</p>
          <p className="text-xs text-gray-500">
            {formatDate(s.date)} · {s.courseName}
          </p>
        </div>
        <div className="text-right">
          {s.amount === 0 ? (
            <>
              <span className="font-bold text-lg text-gray-500">Tie</span>
              <p className="text-xs text-gray-500 font-medium">$0.00</p>
            </>
          ) : (
            <>
              <span className={`font-bold text-lg ${youPay ? "text-red-500" : "text-green-600"}`}>
                {youPay ? "-" : "+"}{formatMoney(s.amount)}
              </span>
              <p className="text-xs text-gray-500 font-medium">
                {youPay ? `You → ${s.counterpartyName}` : `${s.counterpartyName} → You`}
              </p>
            </>
          )}
        </div>
      </div>

      {s.amount > 0 && (<div className="flex gap-2 mt-3">
        {s.venmoHandle && (
          <a
            href={youPay
              ? `venmo://paycharge?txn=pay&recipients=${s.venmoHandle}&amount=${s.amount}&note=DoublePress%20-%20${encodeURIComponent(s.courseName)}`
              : `venmo://paycharge?txn=charge&recipients=${s.venmoHandle}&amount=${s.amount}&note=DoublePress%20-%20${encodeURIComponent(s.courseName)}`}
            className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-xs font-bold"
            data-testid={`pay-venmo-${s.settlementId}`}
          >
            <Smartphone size={14} />
            Venmo
          </a>
        )}
        {s.paypalHandle && (
          <a
            href={`https://www.paypal.com/paypalme/${s.paypalHandle}/${s.amount}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-yellow-50 text-yellow-700 px-3 py-2 rounded-lg text-xs font-bold"
            data-testid={`pay-paypal-${s.settlementId}`}
          >
            <CreditCard size={14} />
            PayPal
          </a>
        )}
        <button
          className="flex items-center gap-1.5 bg-purple-50 text-purple-700 px-3 py-2 rounded-lg text-xs font-bold"
          data-testid={`pay-zelle-${s.settlementId}`}
        >
          <Smartphone size={14} />
          Zelle
        </button>
        <button
          className="flex items-center gap-1.5 bg-gray-50 text-gray-600 px-3 py-2 rounded-lg text-xs font-bold"
          data-testid={`pay-cash-${s.settlementId}`}
        >
          <Banknote size={14} />
          Cash
        </button>
      </div>)}
    </div>
  );
}

function buildRivalryUrl(round: RoundHistoryItem, personId: string): string | null {
  const myTeam = round.participants.find(p => p.personId === personId)?.team;
  if (!myTeam) return null;

  const teamAIds = round.participants.filter(p => p.team === myTeam).map(p => p.personId);
  const teamBIds = round.participants.filter(p => p.team !== myTeam).map(p => p.personId);

  if (teamAIds.length === 1 && teamBIds.length === 1) {
    return `/rivalry/player/${teamAIds[0]}/${teamBIds[0]}`;
  }
  if (teamAIds.length === 2 && teamBIds.length === 2) {
    const sideA = teamAIds.sort().join(",");
    const sideB = teamBIds.sort().join(",");
    const sides = [sideA, sideB].sort();
    return `/rivalry/team/${sides.join("_vs_")}`;
  }
  return null;
}

function CarrierLine({ carriers }: {
  carriers: { carriers: Record<string, { name: string; carries: number }>; shared: number } | null;
}) {
  if (!carriers) return null;
  const entries = Object.entries(carriers.carriers);
  const parts: string[] = [];
  for (const [, val] of entries) {
    parts.push(`${val.name}: ${val.carries}`);
  }
  if (carriers.shared > 0) {
    parts.push(`Shared: ${carriers.shared}`);
  }
  return (
    <p className="text-xs text-gray-500">
      {parts.join(" · ")}
    </p>
  );
}

function RoundDetailSheet({
  round,
  personId,
  onClose,
  onPlayerTap,
  onRivalryTap,
}: {
  round: RoundHistoryItem | null;
  personId: string;
  onClose: () => void;
  onPlayerTap: (personId: string) => void;
  onRivalryTap: (url: string) => void;
}) {
  if (!round) return null;

  const pressLines = round.ledgerLines.filter((l) => l.betType === "press");
  const nassauLines = round.ledgerLines.filter((l) => l.betType !== "press");

  return (
    <Sheet open={!!round} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="text-left pb-3">
          <SheetTitle className="text-lg font-bold text-gray-900">
            {formatMatchup(round.teammates, round.opponents)}
          </SheetTitle>
          <p className="text-xs text-gray-500">
            {round.courseName} · {formatFullDate(round.date)} · {round.matchFormat}
          </p>
        </SheetHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
            <span className="text-sm font-medium text-gray-600">Net Result</span>
            <span
              className={`text-2xl font-black ${
                round.netResult > 0
                  ? "text-green-600"
                  : round.netResult < 0
                  ? "text-red-500"
                  : "text-gray-400"
              }`}
              data-testid="text-detail-net"
            >
              {round.netResult > 0 ? "+" : ""}{formatMoney(round.netResult)}
            </span>
          </div>

          {round.teamHolesWon && round.matchFormat === "2v2" && (
            <div className="bg-emerald-50 rounded-xl p-3" data-testid="detail-holes-won">
              <div className="flex items-center justify-between">
                <div className="text-center flex-1">
                  <p className="text-xs text-gray-500 font-medium">
                    {round.teammates.length > 0
                      ? `You & ${round.teammates[0].displayName}`
                      : "Your Team"}
                  </p>
                  <p className="text-lg font-black text-gray-900">{round.teamHolesWon.myTeamHolesWon}</p>
                </div>
                <div className="text-center px-3">
                  <p className="text-xs text-gray-500 font-semibold uppercase">Holes Won</p>
                  <p className="text-xs text-gray-500">{round.teamHolesWon.tiedHoles} tied</p>
                </div>
                <div className="text-center flex-1">
                  <p className="text-xs text-gray-500 font-medium">
                    {round.opponents.map(o => o.displayName).join(" & ")}
                  </p>
                  <p className="text-lg font-black text-gray-900">{round.teamHolesWon.opponentHolesWon}</p>
                </div>
              </div>
              {(round.teamHolesWon.myCarriers || round.teamHolesWon.opponentCarriers) && (
                <div className="flex items-start justify-between mt-2 pt-2 border-t border-emerald-100" data-testid="detail-carriers">
                  <div className="flex-1 text-center">
                    <CarrierLine carriers={round.teamHolesWon.myCarriers} />
                  </div>
                  <div className="w-8" />
                  <div className="flex-1 text-center">
                    <CarrierLine carriers={round.teamHolesWon.opponentCarriers} />
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
              Players
            </p>
            <div className="grid grid-cols-2 gap-2">
              {round.participants.map((p) => (
                <button
                  key={p.personId}
                  onClick={() => onPlayerTap(p.personId)}
                  className={`rounded-lg p-2.5 text-sm text-left active:opacity-70 transition-opacity ${
                    p.team === "A"
                      ? "bg-blue-50 text-blue-800"
                      : "bg-purple-50 text-purple-800"
                  }`}
                  data-testid={`player-link-${p.personId}`}
                >
                  <span className="font-bold underline decoration-dotted">{p.displayName}</span>
                  {p.handicap && (
                    <span className="text-xs opacity-60 ml-1">({p.handicap})</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {nassauLines.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
                Nassau Breakdown
              </p>
              {nassauLines.map((l, i) => {
                const won = l.toPersonId === personId;
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0"
                  >
                    <span className="text-sm text-gray-600">{betTypeLabel(l.betType)}</span>
                    <span
                      className={`text-sm font-bold ${won ? "text-green-600" : "text-red-500"}`}
                    >
                      {won ? "+" : "-"}{formatMoney(l.amount)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {pressLines.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
                Presses ({pressLines.length})
              </p>
              {pressLines.map((l, i) => {
                const won = l.toPersonId === personId;
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0"
                  >
                    <span className="text-sm text-gray-600">
                      Press{l.holeNumber ? ` (Hole ${l.holeNumber})` : ""}
                    </span>
                    <span
                      className={`text-sm font-bold ${won ? "text-green-600" : "text-red-500"}`}
                    >
                      {won ? "+" : "-"}{formatMoney(l.amount)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}


          {(() => {
            const rivalryUrl = buildRivalryUrl(round, personId);
            if (!rivalryUrl) return null;
            return (
              <button
                onClick={() => {
                  onClose();
                  onRivalryTap(rivalryUrl);
                }}
                className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#064E3B] text-[#FACC15] text-sm font-semibold active:opacity-80 transition-opacity"
                data-testid="button-view-rivalry"
              >
                <Swords className="w-4 h-4" />
                View Rivalry
              </button>
            );
          })()}
        </div>
      </SheetContent>
    </Sheet>
  );
}
